import { useCallback, useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { X } from "lucide-react";

import { AmyChat } from "./AmyChat";
import { cn } from "@/lib/utils";
import amyAvatar from "@/assets/amy-avatar.jpg";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function AmyWidget() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    // Return focus to the trigger for keyboard/screen-reader users.
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  // Escape to close + focus trap while open.
  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;

    // Move initial focus into the dialog.
    requestAnimationFrame(() => {
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const target =
        dialog.querySelector<HTMLElement>("textarea, input") ??
        focusables[0] ??
        dialog;
      target.focus();
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.offsetParent !== null &&
          el.getAttribute("aria-hidden") !== "true",
      );
      if (focusables.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, close]);

  if (!mounted) return null;
  if (pathname === "/ask-amy") return null;

  if (
    pathname.startsWith("/lovable/") ||
    pathname.startsWith("/api/") ||
    pathname.startsWith("/seo-dashboard") ||
    pathname.startsWith("/verification")
  ) {
    return null;
  }

  return (
    <>
      {/* Panel */}
      <div
        className={cn(
          "fixed z-[60] transition-all duration-200",
          "bottom-4 right-4 sm:bottom-6 sm:right-6",
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none",
        )}
        aria-hidden={!open}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Chat with Amy, AI sales assistant"
          tabIndex={-1}
          className={cn(
            "flex h-[min(600px,80vh)] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl outline-none",
          )}
        >
          <AmyChat variant="widget" />
        </div>
      </div>

      {/* Toggle button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat with Amy" : "Open chat with Amy, AI sales assistant"}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(
          "fixed z-[61] bottom-4 right-4 sm:bottom-6 sm:right-6",
          "flex items-center gap-2 rounded-full bg-[#2F5D8A] pl-1.5 pr-4 py-1.5 text-sm font-semibold text-white shadow-xl",
          "hover:bg-[#264c73] transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#2F5D8A]",
        )}
        style={open ? { transform: "translateY(calc(-1 * (min(600px,80vh) + 12px)))" } : undefined}
      >
        {open ? (
          <>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15" aria-hidden="true">
              <X className="h-4 w-4" />
            </span>
            Close
          </>
        ) : (
          <>
            <img
              src={amyAvatar}
              alt=""
              aria-hidden="true"
              width={36}
              height={36}
              className="h-9 w-9 rounded-full object-cover ring-2 ring-white/70"
            />
            Chat with Amy
          </>
        )}
      </button>
    </>
  );
}
